import React from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

// Components
import {
    Paragraph,
    Spacer,
    Image,
    TextButton,
} from '@zillow/constellation';
import { Center, Row, Col } from '@zillow/zrs-comet';

import { GA_EVENTS } from 'app/shared/constants/AnalyticsPaymentsModuleConstants';

import analyticsUtils from 'app/client/utils/analyticsUtils';

import MoneyPig from 'app/shared/images/MoneyPig.svg';

import {
    StyledCard,
    StyledButton,
} from './StyledComponents';

import InlineStyledText from '../InlineStyledText';

const {
    GET_STARTED_EMPTY_STATE_CLICK,
    LEARN_MORE_EMPTY_STATE_CLICK,
} = GA_EVENTS;

const EmptyPaymentsCard = ({
    title,
    description,
    ctaLink,
    ctaPrimaryButton,
    disclaimer,
}) => {
    const dispatch = useDispatch();

    const getDisclaimerText = styledTextList => styledTextList.map(styledText => ({
        styling: 'legal',
        ...styledText,
    }));

    const handleLearnMoreClick = () => {
        dispatch(analyticsUtils.track.GAEventV2(LEARN_MORE_EMPTY_STATE_CLICK));
    };

    const handleStartCollectingRentClick = () => {
        dispatch(analyticsUtils.track.GAEventV2(GET_STARTED_EMPTY_STATE_CLICK));
    };

    return (
        <StyledCard isDisabled={true} data-test="empty-payments-card">
            <Row>
                <Col xs={0} md={1} />
                <Col xs={12} md={10}>
                    <Spacer
                        paddingTop="sm"
                        paddingBottom="md"
                        mediaQuery={{
                            md: { paddingTop: 0 },
                        }}
                    >
                        <Center>
                            <Image
                                className="MoneyPig"
                                data-test="empty-payments-card-money-pig"
                                src={MoneyPig}
                                alt="piggy bank"
                            />
                        </Center>
                    </Spacer>
                    <Center>
                        <Paragraph fontType="bodyHeading">{title}</Paragraph>
                        <Paragraph fontType="bodySmall" paddingTop="sm">{description}</Paragraph>
                    </Center>
                    {
                        (ctaLink.text && ctaLink.url) && (
                        <>
                            <Center marginBottom="xs">
                                <TextButton
                                    as="a"
                                    paddingY="xs"
                                    data-test="empty-payments-card-default-anchor"
                                    href={ctaLink.url}
                                    onClick={handleLearnMoreClick}
                                >
                                    {ctaLink.text}
                                </TextButton>
                            </Center>
                        </>
                        )
                    }
                    <Spacer paddingBottom="sm">
                        <Center>
                            <StyledButton
                                forwardedAs="a"
                                href={ctaPrimaryButton.url}
                                buttonType="primary"
                                fluid={true}
                                onClick={handleStartCollectingRentClick}
                            >
                                {ctaPrimaryButton.text}
                            </StyledButton>
                        </Center>
                    </Spacer>
                    { disclaimer && (
                        <Row>
                            <Col xs={0} md={2} />
                            <Col xs={12} md={8}>
                                <Center>
                                    <InlineStyledText styledTextList={getDisclaimerText(disclaimer)} />
                                </Center>
                            </Col>
                            <Col xs={0} md={2} />
                        </Row>
                    )}
                </Col>
                <Col xs={0} md={1} />
            </Row>
        </StyledCard>
    );
};

EmptyPaymentsCard.propTypes = {
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    ctaLink: PropTypes.shape({
        text: PropTypes.string.isRequired,
        url: PropTypes.string.isRequired,
    }).isRequired,
    ctaPrimaryButton: PropTypes.shape({
        text: PropTypes.string.isRequired,
        url: PropTypes.string.isRequired,
    }).isRequired,
    disclaimer: PropTypes.PropTypes.arrayOf(PropTypes.shape({
        text: PropTypes.string.isRequired,
        url: PropTypes.string,
    })).isRequired,
};

export default EmptyPaymentsCard;
